import { HttpContext } from '../context/HttpContext';
import { useContext, useMemo } from 'react';

const createBillingService = () => {
  return {
    getCurrentSubscription: async (authAxios) => {
      const response = await authAxios.get(
        '/internal/v1/billing/subscriptions/current'
      );
      return response.data.subscription;
    },

    getSubscriptionPlans: async (authAxios, includeCustom = false) => {
      const response = await authAxios.get(
        `/internal/v1/billing/subscription_plans${includeCustom ? '?include_custom=true' : ''}`
      );
      return response.data;
    },

    createSetupIntent: async (authAxios) => {
      const response = await authAxios.post(
        '/internal/v1/billing/setup_intent'
      );
      return response.data;
    },

    attachPaymentMethod: async (
      authAxios,
      paymentMethodId,
      setAsDefault = true
    ) => {
      const response = await authAxios.post(
        '/internal/v1/billing/payment_methods',
        {
          payment_method_id: paymentMethodId,
          set_as_default: setAsDefault,
        }
      );
      return response.data;
    },

    setDefaultPaymentMethod: async (authAxios, paymentMethodId) => {
      const response = await authAxios.patch(
        `/internal/v1/billing/payment_methods/${paymentMethodId}`
      );
      return response.data;
    },

    removePaymentMethod: async (authAxios, paymentMethodId) => {
      const response = await authAxios.delete(
        `/internal/v1/billing/payment_methods/${paymentMethodId}`
      );
      return response.data;
    },

    getPaymentMethods: async (authAxios) => {
      const response = await authAxios.get(
        '/internal/v1/billing/payment_methods'
      );
      return response.data;
    },

    updatePaymentMethod: async (authAxios, paymentMethodId) => {
      const response = await authAxios.post(
        '/internal/v1/billing/payment_methods',
        {
          payment_method_id: paymentMethodId,
        }
      );
      return response.data;
    },

    updateBillingDetails: async (authAxios, billingDetails) => {
      const response = await authAxios.put('/internal/v1/billing/customer', {
        billing_details: billingDetails,
      });
      return response.data;
    },

    updateTaxId: async (authAxios, taxIdType, taxIdValue) => {
      try {
        const response = await authAxios.put(
          '/internal/v1/billing/customer/tax_id',
          {
            tax_id_type: taxIdType,
            tax_id_value: taxIdValue,
          }
        );
        return response.data;
      } catch (error) {
        const code = error?.response?.data?.code;
        const message = error?.response?.data?.error || error.message;
        throw { code, message };
      }
    },

    removeTaxId: async (authAxios) => {
      const response = await authAxios.delete(
        '/internal/v1/billing/customer/tax_id'
      );
      return response.data;
    },

    getBillingInfo: async (authAxios) => {
      const response = await authAxios.get('/internal/v1/billing/customer');
      return response.data;
    },

    createSubscription: async (authAxios, planId) => {
      const response = await authAxios.post(
        '/internal/v1/billing/subscriptions',
        {
          plan_id: planId,
        }
      );
      return response.data;
    },

    cancelSubscription: async (
      authAxios,
      subscriptionId,
      atPeriodEnd = true
    ) => {
      const response = await authAxios.post(
        `/internal/v1/billing/subscriptions/${subscriptionId}/cancel`,
        {
          at_period_end: atPeriodEnd,
        }
      );
      return response.data;
    },

    resumeSubscription: async (authAxios, subscriptionId) => {
      const response = await authAxios.post(
        `/internal/v1/billing/subscriptions/${subscriptionId}/resume`
      );
      return response.data;
    },

    resubscribe: async (authAxios, planId) => {
      const response = await authAxios.post(
        '/internal/v1/billing/subscriptions/resubscribe',
        { plan_id: planId }
      );
      return response.data;
    },

    updateSubscription: async (authAxios, subscriptionId, planId, priceId) => {
      if (!subscriptionId) {
        console.error('Missing subscription ID for update');
        throw new Error('Subscription ID is required');
      }

      if (!planId) {
        console.error('Missing plan ID for update');
        throw new Error('Plan ID is required');
      }

      if (!priceId) {
        console.error('Missing price ID for update');
        throw new Error('Price ID is required');
      }

      const response = await authAxios.post(
        `/internal/v1/billing/subscriptions/${subscriptionId}/update_plan`,
        {
          plan_id: planId,
          price_id: priceId,
        }
      );

      return response.data;
    },

    createCustomPlanRequest: async (
      authAxios,
      requestedEntitlements,
      notes,
      interval
    ) => {
      let billingInterval;
      if (interval === 'year') {
        billingInterval = 'annually';
      } else {
        billingInterval = 'monthly';
      }
      const response = await authAxios.post(
        '/internal/v1/billing/custom-plan-requests',
        {
          requested_entitlements: requestedEntitlements,
          notes,
          billing_interval: billingInterval,
        }
      );
      return response.data;
    },

    getCurrentCustomPlanRequest: async (authAxios) => {
      const response = await authAxios.get(
        '/internal/v1/billing/custom-plan-requests/current'
      );
      return response.data;
    },

    acceptCustomPlan: async (authAxios, requestId, billingInterval) => {
      const response = await authAxios.post(
        `/internal/v1/billing/custom-plan-requests/${requestId}/prepare_subscribe`,
        billingInterval ? { billing_interval: billingInterval } : undefined
      );
      return response.data;
    },

    confirmCustomPlanSubscribe: async (
      authAxios,
      requestId,
      stripeSubscriptionId,
      success,
      error = null
    ) => {
      const response = await authAxios.post(
        `/internal/v1/billing/custom-plan-requests/${requestId}/confirm_subscribe`,
        {
          stripe_subscription_id: stripeSubscriptionId,
          success,
          error,
        }
      );
      return response.data;
    },

    declineCustomPlan: async (authAxios, requestId, rejectionReason = null) => {
      const response = await authAxios.post(
        `/internal/v1/billing/custom-plan-requests/${requestId}/decline`,
        { rejection_reason: rejectionReason }
      );
      return response.data;
    },

    cancelCustomPlanRequest: async (authAxios, requestId) => {
      const response = await authAxios.post(
        `/internal/v1/billing/custom-plan-requests/${requestId}/cancel`
      );
      return response.data;
    },

    requestAmendment: async (
      authAxios,
      requestId,
      requestedEntitlements,
      notes
    ) => {
      const response = await authAxios.post(
        `/internal/v1/billing/custom-plan-requests/${requestId}/request_amendment`,
        {
          requested_entitlements: requestedEntitlements,
          notes,
        }
      );
      return response.data;
    },
  };
};

export const useBillingService = () => {
  const { authAxios } = useContext(HttpContext);
  const service = useMemo(() => createBillingService(), []);

  return useMemo(
    () => ({
      getCurrentSubscription: () => service.getCurrentSubscription(authAxios),
      getSubscriptionPlans: (includeCustom) =>
        service.getSubscriptionPlans(authAxios, includeCustom),
      createSetupIntent: () => service.createSetupIntent(authAxios),
      attachPaymentMethod: (paymentMethodId, setAsDefault) =>
        service.attachPaymentMethod(authAxios, paymentMethodId, setAsDefault),
      setDefaultPaymentMethod: (paymentMethodId) =>
        service.setDefaultPaymentMethod(authAxios, paymentMethodId),
      removePaymentMethod: (paymentMethodId) =>
        service.removePaymentMethod(authAxios, paymentMethodId),
      getPaymentMethods: () => service.getPaymentMethods(authAxios),
      updatePaymentMethod: (paymentMethodId) =>
        service.updatePaymentMethod(authAxios, paymentMethodId),
      updateBillingDetails: (billingDetails) =>
        service.updateBillingDetails(authAxios, billingDetails),
      updateTaxId: (taxIdType, taxIdValue) =>
        service.updateTaxId(authAxios, taxIdType, taxIdValue),
      removeTaxId: () => service.removeTaxId(authAxios),
      getBillingInfo: () => service.getBillingInfo(authAxios),
      createSubscription: (planId) =>
        service.createSubscription(authAxios, planId),
      cancelSubscription: (subscriptionId, atPeriodEnd) =>
        service.cancelSubscription(authAxios, subscriptionId, atPeriodEnd),
      resumeSubscription: (subscriptionId) =>
        service.resumeSubscription(authAxios, subscriptionId),
      resubscribe: (planId) => service.resubscribe(authAxios, planId),
      updateSubscription: (subscriptionId, planId, priceId) =>
        service.updateSubscription(authAxios, subscriptionId, planId, priceId),
      createCustomPlanRequest: (requestedEntitlements, notes, interval) =>
        service.createCustomPlanRequest(
          authAxios,
          requestedEntitlements,
          notes,
          interval
        ),
      getCurrentCustomPlanRequest: () =>
        service.getCurrentCustomPlanRequest(authAxios),
      acceptCustomPlan: (requestId, billingInterval) =>
        service.acceptCustomPlan(authAxios, requestId, billingInterval),
      confirmCustomPlanSubscribe: (
        requestId,
        stripeSubscriptionId,
        success,
        error
      ) =>
        service.confirmCustomPlanSubscribe(
          authAxios,
          requestId,
          stripeSubscriptionId,
          success,
          error
        ),
      declineCustomPlan: (requestId, rejectionReason) =>
        service.declineCustomPlan(authAxios, requestId, rejectionReason),
      cancelCustomPlanRequest: (requestId) =>
        service.cancelCustomPlanRequest(authAxios, requestId),
      requestAmendment: (requestId, requestedEntitlements, notes) =>
        service.requestAmendment(
          authAxios,
          requestId,
          requestedEntitlements,
          notes
        ),
    }),
    [authAxios, service]
  );
};

const billingServiceInstance = {
  getCurrentSubscription: async () => {
    console.warn(
      'Using deprecated billingService singleton - please use useBillingService hook instead'
    );
    return {
      status: 'error',
      error: 'Please wrap component with BillingContextProvider',
    };
  },

  getBillingInfo: async () => {
    console.warn(
      'Using deprecated billingService singleton - please use useBillingService hook instead'
    );
    return {
      subscription: { status: 'error' },
      customer: {},
      payment_method: null,
      payment_methods: [],
    };
  },

  getSubscriptionPlans: async () => [],
  createSetupIntent: async () => ({}),
  attachPaymentMethod: async () => ({}),
  setDefaultPaymentMethod: async () => ({}),
  removePaymentMethod: async () => ({}),
  getPaymentMethods: async () => ({ payment_methods: [] }),
  updatePaymentMethod: async () => ({}),
  updateBillingDetails: async () => ({}),
  updateTaxId: async () => ({}),
  removeTaxId: async () => ({}),
  createSubscription: async () => ({}),
  cancelSubscription: async () => ({}),
  resumeSubscription: async () => ({}),
  resubscribe: async () => ({}),
  updateSubscription: async () => ({}),
  createCustomPlanRequest: async () => ({}),
  getCurrentCustomPlanRequest: async () => ({ custom_plan_request: null }),
  acceptCustomPlan: async () => ({}),
  confirmCustomPlanSubscribe: async () => ({}),
  declineCustomPlan: async () => ({}),
  cancelCustomPlanRequest: async () => ({}),
  requestAmendment: async () => ({}),
};

export default billingServiceInstance;
